import { FC } from 'react'

import {
  SequenceApiError,
  UseQueryResult,
  useMutation,
  useQuery
} from '@sequencehq/api'
import {
  SettingsFormControl,
  SettingsGroup,
  Switch
} from '@sequencehq/core-components'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { StripeRetryScheduleSettings } from './StripeRetryScheduleSettings'
import { RetryAnchor } from './stripeRetrySchedule.helpers'

type StripeSettingsValues = {
  enableAutomaticPayments: boolean
  autoEnableStripePayments: boolean
  retryAnchor?: RetryAnchor | null
  retryOffsetDays?: number[]
}

type PaymentProviderSettingsProps = {
  stripeCollectionSettings: UseQueryResult<
    ({ id: string } & StripeSettingsValues) | null,
    SequenceApiError
  >
  accountPaymentSettings: UseQueryResult<
    {
      id: string
      failureNotifications: {
        merchantNotifications:
          | 'SEND_NOTIFICATIONS'
          | 'DO_NOT_SEND_NOTIFICATIONS'
        customerNotifications:
          | 'SEND_NOTIFICATIONS'
          | 'DO_NOT_SEND_NOTIFICATIONS'
      }
    } | null,
    SequenceApiError
  >
  invoiceRenderSettings: UseQueryResult<
    {
      id: string
      paymentLinkDisplay: string
    } | null,
    SequenceApiError
  >
  stripeCollectionSettingsMutator: ReturnType<
    typeof useMutation<
      typeof dashboardv99990101Client.putStripeCollectionsSettings
    >
  >
  accountPaymentSettingsMutator: ReturnType<
    typeof useMutation<typeof dashboard20240730Client.putAccountPaymentSettings>
  >
  mutateInvoiceRenderSettings: ReturnType<
    typeof useMutation<typeof dashboard20240730Client.putInvoicesRenderSettings>
  >
}

export const PaymentProviderSettings: FC<PaymentProviderSettingsProps> = ({
  stripeCollectionSettings,
  accountPaymentSettings,
  invoiceRenderSettings,
  stripeCollectionSettingsMutator,
  accountPaymentSettingsMutator,
  mutateInvoiceRenderSettings
}) => {
  const stripeIntegration = useQuery(
    dashboard20240730Client.getIntegration,
    'Stripe'
  )

  const goCardlessIntegration = useQuery(
    dashboard20240730Client.getIntegration,
    'GoCardless'
  )

  const isStripeConnected = stripeIntegration.data?.state === 'CONNECTED'
  const isGoCardlessConnected =
    goCardlessIntegration.data?.state === 'CONNECTED'

  if (!isStripeConnected && !isGoCardlessConnected) return null

  const stripeSettings = stripeCollectionSettings.data

  // The PUT endpoint replaces the whole settings object, so every change is sent alongside the
  // current values for the fields it doesn't touch.
  const updateStripeSettings = (overrides: Partial<StripeSettingsValues>) => {
    if (!stripeSettings?.id) return

    stripeCollectionSettingsMutator.mutate({
      id: stripeSettings.id,
      body: {
        enableAutomaticPayments: stripeSettings.enableAutomaticPayments,
        autoEnableStripePayments: stripeSettings.autoEnableStripePayments,
        // retryAnchor is the source of truth for enablement; null leaves retries disabled.
        retryAnchor: stripeSettings.retryAnchor ?? null,
        retryOffsetDays: stripeSettings.retryOffsetDays ?? [],
        ...overrides
      }
    })
  }

  return (
    <>
      <SettingsGroup
        title="Payments"
        isLoading={
          invoiceRenderSettings.isLoading || accountPaymentSettings.isLoading
        }
        rows={2}
      >
        <SettingsFormControl
          label="Display payment links on invoices"
          htmlFor="display-payment-links"
        >
          <Switch
            size="md"
            as="div"
            id="display-payment-links"
            disabled={mutateInvoiceRenderSettings.isPending}
            isChecked={
              invoiceRenderSettings.data?.paymentLinkDisplay ===
              'SHOW_PAYMENT_LINK'
            }
            onChange={value => {
              if (!invoiceRenderSettings.data?.id) return

              mutateInvoiceRenderSettings.mutate({
                id: invoiceRenderSettings.data.id,
                body: {
                  paymentLinkDisplay: value
                    ? 'SHOW_PAYMENT_LINK'
                    : 'HIDE_PAYMENT_LINK'
                }
              })
            }}
          />
        </SettingsFormControl>

        {/* TODO: check if this is a Stripe-only config */}
        <SettingsFormControl
          label="Email customer when payments fail"
          htmlFor="email-customer-payments-fail"
        >
          <Switch
            size="md"
            id="email-customer-payments-fail"
            isChecked={
              accountPaymentSettings.data?.failureNotifications
                .customerNotifications === 'SEND_NOTIFICATIONS'
            }
            disabled={accountPaymentSettingsMutator.isPending}
            onChange={value => {
              if (!accountPaymentSettings.data) return
              accountPaymentSettingsMutator.mutate({
                id: accountPaymentSettings.data.id,
                body: {
                  failureNotifications: {
                    ...accountPaymentSettings.data?.failureNotifications,
                    customerNotifications: value
                      ? 'SEND_NOTIFICATIONS'
                      : 'DO_NOT_SEND_NOTIFICATIONS'
                  }
                }
              })
            }}
          />
        </SettingsFormControl>
      </SettingsGroup>

      {isStripeConnected && (
        <SettingsGroup
          title="Stripe"
          isLoading={
            stripeCollectionSettings.isLoading || stripeIntegration.isLoading
          }
          rows={2}
        >
          <SettingsFormControl
            label="Enable Stripe by default for new schedules"
            htmlFor="auto-enable-stripe-payments"
          >
            <Switch
              size="md"
              id="auto-enable-stripe-payments"
              isChecked={stripeSettings?.autoEnableStripePayments}
              disabled={stripeCollectionSettingsMutator.isPending}
              onChange={value =>
                updateStripeSettings({ autoEnableStripePayments: value })
              }
            />
          </SettingsFormControl>

          <SettingsFormControl
            label="Auto-charge customers via Stripe"
            htmlFor="auto-charge-via-stripe"
          >
            <Switch
              size="md"
              id="auto-charge-via-stripe"
              isChecked={stripeSettings?.enableAutomaticPayments}
              disabled={stripeCollectionSettingsMutator.isPending}
              onChange={value =>
                updateStripeSettings({ enableAutomaticPayments: value })
              }
            />
          </SettingsFormControl>

          {stripeSettings && (
            <StripeRetryScheduleSettings
              settings={{
                retryAnchor: stripeSettings.retryAnchor ?? null,
                retryOffsetDays: stripeSettings.retryOffsetDays ?? []
              }}
              isPending={stripeCollectionSettingsMutator.isPending}
              onUpdate={updateStripeSettings}
            />
          )}
        </SettingsGroup>
      )}
    </>
  )
}
